import type { ReactNode } from "react";
import LoopVideo from "@/components/motion/LoopVideo";
import Lines from "@/components/motion/Lines";
import Scramble from "@/components/motion/Scramble";
import { serviceVideo } from "@/data/serviceCatalog";

/**
 * The opening of every inner page, in the homepage's language: an ink band,
 * a mono label, a two-line display headline (solid, then outlined) and an
 * optional ambient clip behind it. The headline is the page's <h1>.
 */
type PageHeroProps = {
  label: string;
  lead: string;
  tail?: string;
  intro?: ReactNode;
  /** A clip from public/media, by name (see serviceCatalog). */
  media?: string;
  children?: ReactNode;
};

const PageHero = ({ label, lead, tail, intro, media, children }: PageHeroProps) => {
  const video = media ? serviceVideo(media) : null;
  return (
    <section className="uncontained relative overflow-hidden bg-ink px-5 pb-16 pt-36 text-white md:px-8 md:pb-24 md:pt-48">
      {video && (
        <>
          <LoopVideo src={video.src} poster={video.poster} priority className="absolute inset-0 opacity-40" />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        </>
      )}
      <div className="relative">
        <p className="label-mono mb-6 text-white/55">
          <Scramble text={label} />
        </p>
        <Lines
          as="h1"
          lines={tail ? [lead, tail] : [lead]}
          lineClassName={(i) => (i === 1 ? "text-outline" : "")}
          className="display text-[clamp(3.2rem,11vw,12rem)]"
        />
        {(intro || children) && (
          <div className="mt-10 grid gap-8 border-t border-white/20 pt-8 md:mt-14 md:grid-cols-12">
            {intro && <div className="max-w-xl text-lg leading-relaxed text-white/75 md:col-span-6">{intro}</div>}
            {children && <div className="md:col-span-6">{children}</div>}
          </div>
        )}
      </div>
    </section>
  );
};

export default PageHero;
